import { FC, useState } from "react";

import Stack from "@mui/material/Stack";
import {
  formatDateToFullDate,
  isBankDebitPaymentMethod,
  isChargeDayCustomizable,
  toChargeTimezoneDateTime,
  FormType,
  IsoDate,
  OrganizationCountry,
  PaymentMethod,
  SubscriptionRecurrenceInterval,
} from "@simplyk/common";

import { CommandFromQuery, DonorPaymentControl, PaymentFromQuery, SubscriptionFromQuery } from "../types";

import { CancelRenewalMenu } from "./CancelRenewalMenu/CancelRenewalMenu";
import { CancelSubscriptionDialog } from "./CancelSubscriptionDialog/CancelSubscriptionDialog";
import { CANCELLATION_REASON_TO_TRACKING, CancellationReason } from "./CancelSubscriptionDialog/helpers/constants";
import { ScheduleEndDateDialog } from "./ScheduleEndDateDialog/ScheduleEndDateDialog";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { trpc } from "@/common/helpers/trpc";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";
import { useTranslate } from "@/common/hooks/useTranslate";
import { EditChargeDateDialog } from "@/components/EditChargeDateDialog/EditChargeDateDialog";
import { ChargeDateChange } from "@/components/EditChargeDateDialog/helpers/useEditChargeDate";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import {
  DashboardIconBlock,
  DashboardIconBlockVibe,
} from "@/dashboard-design-system/DashboardIconBlock/DashboardIconBlock";
import { DashboardIcon } from "@/dashboard-design-system/Icon/DashboardIcon";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { Autorenew, Bank, CreditCard, ExternalLink, Gift, ZeffyMark } from "@/icons/outlined";
import { IconElement } from "@/icons/types";

interface DetailItemProps {
  icon: IconElement;
  iconVibe?: DashboardIconBlockVibe;
  label: string;
  value: React.ReactNode;
  action?: React.ReactNode;
}

const DetailItem: FC<DetailItemProps> = ({ icon, iconVibe = "neutral", label, value, action }) => (
  <Stack direction="row" gap={2} alignItems="flex-start">
    <DashboardIconBlock vibe={iconVibe} size="medium" sx={{ mt: 0.25, flexShrink: 0 }}>
      {icon}
    </DashboardIconBlock>
    <Stack sx={{ minWidth: 0, flex: 1 }}>
      <DashboardTypography variant="body2" vibe="text-neutral-quiet">
        {label}
      </DashboardTypography>
      <DashboardTypography variant="body2" vibe="text-neutral-moderate" component="div">
        {value}
      </DashboardTypography>
    </Stack>
    {action && <Stack sx={{ flexShrink: 0, alignSelf: "center" }}>{action}</Stack>}
  </Stack>
);

interface PaymentDetailsProps {
  paymentMethodLabel: string;
  tipAmount: number;
  formattedTip: string;
  isRecurring: boolean;
  isActiveSubscription: boolean;
  payment: PaymentFromQuery;
  command: CommandFromQuery | null | undefined;
  subscription: SubscriptionFromQuery | null | undefined;
  organizationCountry: OrganizationCountry | undefined;
  formLink: string;
  adjustTipLink: string;
  isRecurringPayment: boolean;
  cancelSubscription: (
    commandId: string,
    cancellationReason?: CancellationReason,
    cancellationFeedback?: string
  ) => Promise<boolean | void>;
  cancelSubscriptionIsLoading: boolean;
  editChargeDate: (commandId: string, newChargeDate: ChargeDateChange) => Promise<boolean | void>;
  editChargeDateIsLoading: boolean;
  scheduleSubscriptionEnd: (commandId: string, endDate: IsoDate) => Promise<boolean>;
  scheduleSubscriptionEndIsLoading: boolean;
  formattedAmount: string;
  organizationName: string;
  updateCreditCard: (subscriptionId: string, country: OrganizationCountry) => () => Promise<void>;
  hasGiftAidConfirmation: boolean | null | undefined;
  hasAutomaticRenewal: boolean;
  isMembership: boolean;
  initialControl?: DonorPaymentControl;
}

export const PaymentDetails: FC<PaymentDetailsProps> = ({
  paymentMethodLabel,
  tipAmount,
  formattedTip,
  isRecurring,
  isActiveSubscription,
  payment,
  command,
  subscription,
  organizationCountry,
  formLink,
  adjustTipLink,
  isRecurringPayment,
  cancelSubscription,
  cancelSubscriptionIsLoading,
  editChargeDate,
  editChargeDateIsLoading,
  scheduleSubscriptionEnd,
  scheduleSubscriptionEndIsLoading,
  formattedAmount,
  organizationName,
  updateCreditCard,
  hasGiftAidConfirmation,
  hasAutomaticRenewal,
  isMembership,
  initialControl,
}) => {
  const { t } = useTranslate();
  const { locale } = useLocaleContext();
  const { logAmplitudeEvent } = useAmplitude();
  const [scheduleEndDateDialogOpen, setScheduleEndDateDialogOpen] = useState(false);

  const getRenewalLabel = () => {
    switch (payment.recurrenceInterval) {
      case SubscriptionRecurrenceInterval.Weekly:
        return t("dashboard", "donorTransactionDetailDrawer.renewsWeekly");
      case SubscriptionRecurrenceInterval.BiWeekly:
        return t("dashboard", "donorTransactionDetailDrawer.renewsBiWeekly");
      case SubscriptionRecurrenceInterval.Monthly:
        return t("dashboard", "donorTransactionDetailDrawer.renewsMonthly");
      case SubscriptionRecurrenceInterval.Quarterly:
        return t("dashboard", "donorTransactionDetailDrawer.renewsQuarterly");
      case SubscriptionRecurrenceInterval.Yearly:
      default:
        return t("dashboard", "donorTransactionDetailDrawer.renewsYearly");
    }
  };
  const renewalLabel = getRenewalLabel();
  const endDateSchedulableInterval =
    payment.formType === FormType.DonationForm &&
    payment.recurrenceInterval &&
    isChargeDayCustomizable(payment.recurrenceInterval)
      ? payment.recurrenceInterval
      : null;
  const { data: stripeSchedule } = trpc.retrieveDonorSubscriptionStripeSchedule.useQuery(
    { id: command?.id ?? "" },
    { enabled: Boolean(scheduleEndDateDialogOpen && command?.id && endDateSchedulableInterval) }
  );
  const scheduledEndAt = subscription?.scheduledEndAtUtc;
  const scheduledEndDay = scheduledEndAt
    ? (toChargeTimezoneDateTime(new Date(scheduledEndAt), subscription?.userTimezone).toISODate() ?? "")
    : "";
  const hasRenewalBeforeEnd =
    !scheduledEndAt || !payment.nextRenewalAt || new Date(payment.nextRenewalAt) < new Date(scheduledEndAt);
  const canManageRenewal = isRecurring && isActiveSubscription && Boolean(command?.id);
  const canEditChargeDate =
    canManageRenewal &&
    Boolean(payment.nextRenewalAt) &&
    Boolean(payment.recurrenceInterval) &&
    payment.subscriptionStatus === "Active";
  const canChangePaymentMethod =
    isRecurring &&
    isActiveSubscription &&
    payment.paymentMethod !== PaymentMethod.Sepa &&
    command?.paymentMethod !== PaymentMethod.Sepa &&
    Boolean(subscription?.stripeSubscriptionId) &&
    Boolean(organizationCountry);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(initialControl === "cancel_renewal" && canManageRenewal);
  const [editChargeDateDialogOpen, setEditChargeDateDialogOpen] = useState(
    initialControl === "edit_charge_date" && canEditChargeDate
  );

  const renderRenewalValue = () => {
    if (isRecurring && isActiveSubscription && payment.nextRenewalAt) {
      return (
        <Stack>
          <Stack direction="row" alignItems="center" gap={0.5}>
            <DashboardTypography variant="body2" vibe="text-positive-moderate">
              {renewalLabel}
            </DashboardTypography>
            <DashboardIcon size="small" vibe="text-positive-quiet">
              <Autorenew />
            </DashboardIcon>
          </Stack>
          {hasRenewalBeforeEnd && (
            <DashboardTypography variant="body2" vibe="text-neutral-moderate">
              {t("dashboard", "donorTransactionDetailDrawer.nextPaymentOn", {
                date: formatDateToFullDate(payment.nextRenewalAt, locale, subscription?.userTimezone),
              })}
            </DashboardTypography>
          )}
          {scheduledEndAt && (
            <DashboardTypography variant="body2" vibe="text-neutral-moderate" data-test="renewal-ends-on">
              {t("dashboard", "donorTransactionDetailDrawer.endsOn", {
                date: formatDateToFullDate(scheduledEndAt, locale, subscription?.userTimezone),
              })}
            </DashboardTypography>
          )}
        </Stack>
      );
    }
    if (payment.subscriptionStatus === "Ended") {
      return t("dashboard", "donorTransactionDetailDrawer.renewalEnded");
    }
    return t("dashboard", "donorTransactionDetailDrawer.noRenewal");
  };

  const handleOpenCancelDialog = () => {
    logAmplitudeEvent(AmplitudeEvents.DonorTransactionDrawerClickStopDonation);
    setCancelDialogOpen(true);
  };

  const handleConfirmCancel = async (data: {
    cancellationReason: CancellationReason;
    cancellationFeedback: string;
  }) => {
    if (!command?.id) {
      return;
    }
    const success = await cancelSubscription(command.id, data.cancellationReason, data.cancellationFeedback);
    if (success) {
      logAmplitudeEvent(AmplitudeEvents.RecurringPaymentStopped, {
        location: "Donor Space",
        campaign_type: isMembership ? "membership" : "donation",
        reason: CANCELLATION_REASON_TO_TRACKING[data.cancellationReason],
      });
    }
  };

  const handleOpenEditChargeDateDialog = () => {
    logAmplitudeEvent(AmplitudeEvents.DonorTransactionDrawerClickEditChargeDate);
    setEditChargeDateDialogOpen(true);
  };

  const handleConfirmEditChargeDate = async (newChargeDate: ChargeDateChange) => {
    if (!command?.id) {
      return false;
    }
    const success = await editChargeDate(command.id, newChargeDate);
    if (success) {
      logAmplitudeEvent(AmplitudeEvents.RecurringChargeDateEdited, {
        location: "Donor Space",
        campaign_type: isMembership ? "membership" : "donation",
      });
    }
    return success;
  };

  const handleConfirmScheduleEndDate = async (endDate: IsoDate) => {
    if (!command?.id) {
      return false;
    }
    const success = await scheduleSubscriptionEnd(command.id, endDate);
    if (success) {
      logAmplitudeEvent(AmplitudeEvents.RecurringPaymentEndScheduled);
    }
    return success;
  };

  const renderRenewalAction = () => {
    if (canManageRenewal) {
      return (
        <Stack direction="row" gap={1}>
          {canEditChargeDate && (
            <DashboardButton variant="outlined" size="small" vibe="neutral" onClick={handleOpenEditChargeDateDialog}>
              {t("dashboard", "donorTransactionDetailDrawer.editChargeDate")}
            </DashboardButton>
          )}
          {endDateSchedulableInterval && payment.nextRenewalAt ? (
            <CancelRenewalMenu
              isLoading={cancelSubscriptionIsLoading || scheduleSubscriptionEndIsLoading}
              onScheduleEndDate={() => setScheduleEndDateDialogOpen(true)}
              onCancelImmediately={handleOpenCancelDialog}
            />
          ) : (
            <DashboardButton
              variant="outlined"
              size="small"
              vibe="danger"
              isLoading={cancelSubscriptionIsLoading}
              onClick={handleOpenCancelDialog}
            >
              {t("dashboard", "donorTransactionDetailDrawer.cancelRenewal")}
            </DashboardButton>
          )}
        </Stack>
      );
    }

    if (isMembership && !hasAutomaticRenewal && formLink) {
      return (
        <DashboardButton
          variant="tonal"
          size="small"
          vibe="positive"
          endIcon={<ExternalLink />}
          href={formLink}
          target="_blank"
          onClick={() => logAmplitudeEvent(AmplitudeEvents.MembershipRenewalStarted, { location: "Donor Space" })}
        >
          {t("dashboard", "donorTransactionDetailDrawer.renewMembership")}
        </DashboardButton>
      );
    }

    return null;
  };

  return (
    <>
      <Stack gap={2}>
        <DashboardTypography variant="subtitle2" vibe="text-neutral-quiet" sx={{ textTransform: "uppercase" }}>
          {t("dashboard", "donorTransactionDetailDrawer.paymentDetails")}
        </DashboardTypography>

        <Stack gap={2}>
          <DetailItem
            icon={isBankDebitPaymentMethod(payment.paymentMethod) ? <Bank /> : <CreditCard />}
            label={t("dashboard", "donorTransactionDetailDrawer.method")}
            value={paymentMethodLabel}
            action={
              canChangePaymentMethod &&
              subscription?.stripeSubscriptionId &&
              organizationCountry && (
                <DashboardButton
                  variant="outlined"
                  size="small"
                  vibe="neutral"
                  autoFocus={initialControl === "update_payment_method"}
                  onClick={updateCreditCard(subscription.stripeSubscriptionId, organizationCountry)}
                >
                  {t("dashboard", "donorTransactionDetailDrawer.changePaymentMethod")}
                </DashboardButton>
              )
            }
          />

          {tipAmount > 0 && (
            <DetailItem
              icon={<ZeffyMark />}
              label={t("dashboard", "donorTransactionDetailDrawer.zeffyTip")}
              value={
                isRecurring
                  ? `${formattedTip} ${t("dashboard", "donorTransactionDetailDrawer.perTransaction")}`
                  : formattedTip
              }
              action={
                adjustTipLink && (
                  <DashboardButton
                    variant="outlined"
                    size="small"
                    vibe="neutral"
                    href={adjustTipLink}
                    target="_blank"
                    autoFocus={initialControl === "adjust_tip"}
                    onClick={() => {
                      logAmplitudeEvent(
                        isRecurringPayment
                          ? AmplitudeEvents.RecurringTipAdjustmentStarted
                          : AmplitudeEvents.TipRefundStarted
                      );
                    }}
                  >
                    {t("dashboard", "donorTransactionDetailDrawer.adjustTip")}
                  </DashboardButton>
                )
              }
            />
          )}

          {(isRecurring || payment.formType !== FormType.Ticketing) && (
            <Stack sx={{ pb: 1 }}>
              <DetailItem
                icon={<Autorenew />}
                iconVibe={isRecurring && isActiveSubscription ? "positive" : "neutral"}
                label={t("dashboard", "donorTransactionDetailDrawer.renewal")}
                value={renderRenewalValue()}
                action={renderRenewalAction()}
              />
            </Stack>
          )}

          {hasGiftAidConfirmation && (
            <DetailItem
              icon={<Gift />}
              label={t("dashboard", "donorTransactionDetailDrawer.hasGiftAidConfirmation")}
              value={t("dashboard", "common.yes")}
            />
          )}
        </Stack>
      </Stack>

      <CancelSubscriptionDialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        onConfirm={handleConfirmCancel}
        isLoading={cancelSubscriptionIsLoading}
        isMembership={isMembership}
        currentPeriodEnd={payment.nextRenewalAt}
      />

      {payment.nextRenewalAt && payment.recurrenceInterval && (
        <EditChargeDateDialog
          open={editChargeDateDialogOpen}
          onClose={() => setEditChargeDateDialogOpen(false)}
          onConfirm={handleConfirmEditChargeDate}
          isLoading={editChargeDateIsLoading}
          variant="donor"
          nextChargeDate={payment.nextRenewalAt}
          recurrenceInterval={payment.recurrenceInterval}
          chargeAmount={subscription?.totalAmount}
          currency={payment.currency}
          timezone={subscription?.userTimezone}
        />
      )}

      {payment.nextRenewalAt && endDateSchedulableInterval && (
        <ScheduleEndDateDialog
          open={scheduleEndDateDialogOpen}
          onClose={() => setScheduleEndDateDialogOpen(false)}
          onConfirm={handleConfirmScheduleEndDate}
          isLoading={scheduleSubscriptionEndIsLoading}
          nextChargeDate={stripeSchedule?.nextChargeDate ?? payment.nextRenewalAt}
          recurrenceInterval={endDateSchedulableInterval}
          billingAnchorDayOfMonth={stripeSchedule?.billingAnchorDayOfMonth}
          formattedAmount={formattedAmount}
          organizationName={organizationName}
          timezone={subscription?.userTimezone}
          defaultEndDate={scheduledEndDay}
          key={scheduledEndDay}
        />
      )}
    </>
  );
};
